import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    ebitda: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => setMenuOpen(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      const response = await fetch('https://readdy.ai/api/form/d6kiiqh4a3uojp5fft8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString()
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', ebitda: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="w-full px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src="https://public.readdy.ai/ai/img_res/3f9ff8ee-84b4-407c-8fe3-2f6ced31ed00.png" alt="Chris Okotete-Peters" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0" />
              <span className={`text-sm sm:text-base font-semibold truncate ${isScrolled || menuOpen ? 'text-gray-900' : 'text-white'}`}>
                Chris Okotete-Peters
                <span className="hidden sm:inline"> | Business Acquisitions</span>
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>About</a>
              <a href="#criteria" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>Criteria</a>
              <a href="#why-me" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>Why Chris</a>
              <a href="#testimonials" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>Testimonials</a>
              <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>FAQ</a>
              <a href="#contact" className="bg-[#1a2332] text-white px-5 py-2.5 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap">Submit Opportunity</a>
            </div>

            {/* Mobile Hamburger */}
            <button
              className={`lg:hidden p-2 rounded-md transition-colors cursor-pointer ${isScrolled || menuOpen ? 'text-gray-900' : 'text-white'}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4 flex flex-col gap-4">
              <a href="#about" onClick={handleNavClick} className="text-sm font-medium text-gray-700 hover:text-gray-900 py-1">About</a>
              <a href="#criteria" onClick={handleNavClick} className="text-sm font-medium text-gray-700 hover:text-gray-900 py-1">Criteria</a>
              <a href="#why-me" onClick={handleNavClick} className="text-sm font-medium text-gray-700 hover:text-gray-900 py-1">Why Chris</a>
              <a href="#testimonials" onClick={handleNavClick} className="text-sm font-medium text-gray-700 hover:text-gray-900 py-1">Testimonials</a>
              <a href="#faq" onClick={handleNavClick} className="text-sm font-medium text-gray-700 hover:text-gray-900 py-1">FAQ</a>
              <a href="#contact" onClick={handleNavClick} className="bg-[#1a2332] text-white px-5 py-3 rounded-lg text-sm font-semibold text-center whitespace-nowrap">Submit Opportunity</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-r from-[#1a2332] via-[#1f2937] to-[#2d3748]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="w-full px-4 sm:px-6 lg:px-12 py-28 sm:py-32 relative z-10">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Text */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <div className="inline-block mb-4 sm:mb-6">
                <span className="text-[#d4af37] text-xs sm:text-sm font-semibold tracking-[2px] uppercase">Private Equity Buyer | Chicago, IL</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 sm:mb-8">
                Acquiring Established Businesses Built on Strong Fundamentals
              </h1>
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0">
                Technology executive seeking profitable businesses with $500K–$2M EBITDA. Your legacy. Professional stewardship. Strategic growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#criteria" className="bg-[#b8941f] text-white px-6 sm:px-8 py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-[#a07d1a] transition-all shadow-lg hover:shadow-xl whitespace-nowrap text-center">View Acquisition Criteria</a>
                <a href="#contact" className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap text-center">Schedule Consultation</a>
              </div>
            </div>
            {/* Photo */}
            <div className="flex justify-center order-1 lg:order-2">
              <div className="relative">
                <div className="w-52 h-52 sm:w-64 sm:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden shadow-[0_0_60px_rgba(255,255,255,0.15)]">
                  <img src="https://static.readdy.ai/image/e6b1c2d27ffa53fe15e3f47a43259f63/986f4ebdbb1b653b4c9214900931a7d0.png" alt="Chris Okotete-Peters" className="w-full h-full object-cover object-top" />
                </div>
                <div className="mt-4 sm:mt-6 text-center">
                  <p className="text-white text-sm font-medium">IT Director | ERP Consultant |&nbsp;Digital Transformation</p>
                  <a href="https://www.linkedin.com/in/profile1197" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-2 text-[#d4af37] text-sm mt-2 hover:text-[#b8941f] transition-colors">
                    <i className="ri-linkedin-fill text-lg"></i>
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-[#faf8f5] py-20 sm:py-28 lg:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-20">
            <div className="lg:col-span-2 text-center lg:text-left">
              <span className="text-[#d4af37] text-xs font-bold tracking-[3px] uppercase">Who I Am</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2332] mt-4 sm:mt-6 leading-tight">
                Building a Portfolio of Enduring Businesses
              </h2>
              <div className="w-16 h-0.5 bg-[#d4af37] mt-6 mx-auto lg:mx-0"></div>
            </div>
            <div className="lg:col-span-3">
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-5 font-semibold">
                I'm Chris Okotete-Peters, currently based in Chicago, Illinois. I'm married with three kids, and I work as an IT Director and ERP Consultant, where I lead technology strategy, ERP implementations, and digital transformation initiatives for mid-size companies.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-5">
                My background is heavily focused on technology, operations, and systems optimization. I've spent most of my career helping businesses scale through better infrastructure, automation, and process improvements.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-8">
                One of my major goals over the next few years is to transition away from the traditional corporate 9–5 and build a portfolio of businesses that I own. My vision is to own my own businesses or companies and to become a <span className="text-[#b8941f] font-semibold">strategic owner</span>, focusing on <span className="text-[#b8941f] font-semibold">capital allocation</span>, <span className="text-[#b8941f] font-semibold">growth strategy</span>, and <span className="text-[#b8941f] font-semibold">acquisitions</span>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-trophy-line text-2xl text-[#b8941f]"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">20+ Years Technology Leadership</span>
                </div>
                <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-2xl text-[#b8941f]"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">ERP Implementation Expert</span>
                </div>
                <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-[#b8941f]"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">M&amp;A Experience</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Acquisition Criteria Section */}
      <section id="criteria" className="bg-gradient-to-b from-white to-gray-50 py-20 sm:py-28 lg:py-36">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <div className="inline-block border border-gray-300 rounded-full px-5 py-2 mb-5">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">What I'm Looking For</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2332] mb-4">Acquisition Criteria</h2>
            <p className="text-base sm:text-lg text-gray-600">Transparent parameters for a smooth transaction</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'EBITDA Range',
                value: '$500K – $2M',
                valueClass: 'text-3xl sm:text-4xl font-bold text-[#d4af37]',
                desc: 'Proven profitability with stable cash flow'
              },
              {
                icon: 'ri-grid-line',
                title: 'Industry Focus',
                value: 'Agnostic',
                valueClass: 'text-2xl sm:text-3xl font-bold text-[#1a2332]',
                list: ['Technology-enabled businesses', 'Recurring revenue models', 'Strong fundamentals']
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Geographic Scope',
                value: 'Flexible',
                valueClass: 'text-2xl sm:text-3xl font-bold text-[#1a2332]',
                desc: 'Open to opportunities across the United States'
              },
              {
                icon: 'ri-team-line',
                title: 'Management',
                value: 'Team in Place',
                valueClass: 'text-2xl sm:text-3xl font-bold text-[#1a2332]',
                desc: 'Existing leadership for absentee ownership model'
              }
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 sm:p-10 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 bg-[#d4af37] rounded-full flex items-center justify-center mb-5">
                  <i className={`${card.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1a2332] mb-3">{card.title}</h3>
                <div className={`${card.valueClass} mb-4`}>{card.value}</div>
                {card.desc && <p className="text-sm sm:text-base text-gray-600">{card.desc}</p>}
                {card.list && (
                  <ul className="text-sm text-gray-600 space-y-2">
                    {card.list.map((item, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <i className="ri-check-line text-[#d4af37] mt-0.5 flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Industries Section */}
      <section id="industries" className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2332] mb-4 sm:mb-5">Target Industries</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">While I have a strong background in technology, I'm open to businesses across various sectors as long as the fundamentals are solid and there's potential for strategic growth.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: 'ri-computer-line', title: 'IT Services & MSPs', desc: 'Managed service providers, IT consulting firms, and technology support businesses with recurring revenue streams.' },
              { icon: 'ri-tools-line', title: 'Technology-Enabled Services', desc: 'Service businesses leveraging technology for competitive advantage and operational efficiency.' },
              { icon: 'ri-building-line', title: 'Professional Services', desc: 'Consulting firms, specialized service providers, and businesses with established client relationships.' },
              { icon: 'ri-temp-hot-line', title: 'HVAC', desc: 'Heating, ventilation, and air conditioning companies with established service contracts and recurring maintenance revenue.' },
              { icon: 'ri-flashlight-line', title: 'Plumbing & Electrical', desc: 'Licensed plumbing and electrical contractors with strong local reputations and steady commercial or residential demand.' },
              { icon: 'ri-sparkling-line', title: 'Commercial Cleaning', desc: 'Commercial janitorial and facility cleaning businesses with long-term contracts and scalable operations.' },
              { icon: 'ri-recycle-line', title: 'Waste Management', desc: 'Waste collection, recycling, and disposal businesses with route-based recurring revenue and essential service demand.' },
              { icon: 'ri-truck-line', title: 'Manufacturing & Distribution', desc: 'Light manufacturing and distribution companies with proven processes, loyal customer bases, and strong cash flow.' },
              { icon: 'ri-pie-chart-line', title: 'Other Strong Fundamentals', desc: 'Any business with proven cash flow, solid management, and clear growth opportunities.' }
            ].map((item, i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-all">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#d4af37]/10 rounded-xl flex items-center justify-center mb-4 sm:mb-5">
                  <i className={`${item.icon} text-2xl sm:text-3xl text-[#b8941f]`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#1a2332] mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Approach Section */}
      <section id="portfolio" className="bg-[#1a2332] py-24 sm:py-32 lg:py-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 sm:mb-20">
            <span className="text-[#d4af37] text-xs font-bold tracking-[3px] uppercase">How We Work Together</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-5 mb-4">A Respectful, Professional Process</h2>
            <p className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto">Designed to honor your business legacy while ensuring smooth transition</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 sm:gap-6">
            {[
              { num: '01', icon: 'ri-hand-heart-line', title: 'Initial Contact', desc: '30-minute call to understand your business and goals', week: 'Week 1' },
              { num: '02', icon: 'ri-search-line', title: 'Due Diligence', desc: 'Financial analysis, operations assessment, team evaluation', week: 'Weeks 2–4' },
              { num: '03', icon: 'ri-calculator-line', title: 'Valuation', desc: 'Transparent valuation based on market standards', week: 'Week 5' },
              { num: '04', icon: 'ri-file-text-line', title: 'Negotiation', desc: 'Flexible structure to meet your needs', week: 'Weeks 6–8' },
              { num: '05', icon: 'ri-arrow-right-line', title: 'Transition', desc: 'Respectful transition with your involvement level', week: 'Weeks 9–12' }
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
                <div className="w-11 h-11 bg-[#d4af37] rounded-full flex items-center justify-center text-white font-bold text-base mb-4">{step.num}</div>
                <div className="w-10 h-10 flex items-center justify-center mb-3">
                  <i className={`${step.icon} text-3xl text-[#b8941f]`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[#1a2332] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{step.desc}</p>
                <span className="text-xs font-semibold text-[#b8941f]">{step.week}</span>
              </div>
            ))}
          </div>
          <div className="mt-14 sm:mt-20 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-5">My Portfolio Vision</h3>
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-5">
              Long term, my vision is to build a holding company that acquires and grows profitable small to mid-size businesses, while maintaining strong leadership teams in place. I'm looking to acquire businesses where I can add strategic value through my technology and operations background, without disrupting what already works.
            </p>
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
              Ideally, I want to acquire businesses that already have strong teams and systems, allowing me to focus on scaling and eventually building a portfolio of multiple companies over time. Your business will be in good hands, with a focus on preserving what you've built while positioning it for continued success.
            </p>
          </div>
        </div>
      </section>

      {/* ── WHY SELL TO ME ── */}
      <section id="why-me" className="bg-[#faf8f5] py-20 sm:py-28 lg:py-36">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 sm:mb-20">
            <span className="text-[#d4af37] text-xs font-bold tracking-[3px] uppercase">The Difference</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2332] mt-4 mb-4 leading-tight">
              Why Sell to Chris vs. a PE Firm or Broker?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Not all buyers are created equal. Here's what makes working directly with me a fundamentally different — and better — experience.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto mb-16">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-500 uppercase tracking-wider w-1/3"></th>
                  <th className="py-4 px-6 text-center w-1/3">
                    <div className="inline-flex flex-col items-center gap-1">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37] rounded-full">
                        <i className="ri-user-star-line text-white text-xl"></i>
                      </div>
                      <span className="text-base font-bold text-[#1a2332]">Chris</span>
                      <span className="text-xs text-[#b8941f] font-semibold">Direct Buyer</span>
                    </div>
                  </th>
                  <th className="py-4 px-6 text-center w-1/3">
                    <div className="inline-flex flex-col items-center gap-1">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full">
                        <i className="ri-building-4-line text-gray-500 text-xl"></i>
                      </div>
                      <span className="text-base font-bold text-gray-500">PE Firm / Broker</span>
                      <span className="text-xs text-gray-400 font-semibold">Traditional Route</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Decision Maker', chris: 'You speak directly with the buyer', other: 'Multiple layers of committees' },
                  { label: 'Confidentiality', chris: 'Strict NDA from day one', other: 'Info shared across deal teams' },
                  { label: 'Speed to Close', chris: '60–90 days typical', other: '6–18 months common' },
                  { label: 'Broker Fees', chris: 'Zero — no middleman', other: '5–10% of deal value' },
                  { label: 'Employee Treatment', chris: 'Preserve culture & retain team', other: 'Restructuring & layoffs common' },
                  { label: 'Seller Involvement', chris: 'Your preferred level of involvement', other: 'Often forced exit post-close' },
                  { label: 'Deal Flexibility', chris: 'Creative structures (earnouts, equity)', other: 'Rigid, template-driven terms' },
                  { label: 'Long-term Vision', chris: 'Grow & steward your legacy', other: 'Flip for profit in 3–5 years' },
                ].map((row, i) => (
                  <tr key={i} className={`border-t border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-[#faf8f5]'}`}>
                    <td className="py-4 px-6 text-sm font-semibold text-gray-700">{row.label}</td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-fill text-[#b8941f] text-lg"></i>
                        </div>
                        <span className="text-sm text-gray-800 font-medium">{row.chris}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-close-circle-line text-gray-400 text-lg"></i>
                        </div>
                        <span className="text-sm text-gray-500">{row.other}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Advantage Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: 'You Keep Your Legacy', desc: 'I acquire businesses to grow them — not strip them. Your brand, your team, and the culture you\'ve built will be respected and preserved.' },
              { icon: 'ri-hand-coin-line', title: 'No Broker Fees', desc: 'Working directly with me means zero broker commissions. More of the deal value stays in your pocket where it belongs.' },
              { icon: 'ri-speed-line', title: 'Fast, Decisive Process', desc: 'As the sole decision-maker, I move quickly. No investment committees, no endless approval chains — just a clear, efficient process.' },
              { icon: 'ri-lock-password-line', title: 'Total Confidentiality', desc: 'Your employees, customers, and competitors won\'t know a thing until you\'re ready. I sign NDAs immediately and take discretion seriously.' },
              { icon: 'ri-exchange-dollar-line', title: 'Flexible Deal Structures', desc: 'Seller financing, earnouts, equity rollovers — I\'m open to creative structures that work for your financial and personal goals.' },
              { icon: 'ri-user-heart-line', title: 'A Real Person, Not a Fund', desc: 'You\'re not selling to a faceless institution. You\'re working with Chris — a family man, a professional, and someone who genuinely cares about what you\'ve built.' },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:-translate-y-1">
                <div className="w-12 h-12 bg-[#1a2332] rounded-xl flex items-center justify-center mb-5">
                  <i className={`${card.icon} text-2xl text-[#d4af37]`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-3">{card.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="bg-[#1a2332] py-20 sm:py-28 lg:py-36 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#d4af37] rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d4af37] rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-14 sm:mb-20">
            <span className="text-[#d4af37] text-xs font-bold tracking-[3px] uppercase">Social Proof</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 mb-4">What Colleagues & Partners Say</h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
              Don't just take my word for it. Here's what people who've worked closely with me have to say.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
            {[
              {
                quote: "Chris is one of the most methodical and trustworthy professionals I've worked with. When he takes on a project — or a business — he sees it through with integrity and precision. Any seller would be in excellent hands.",
                name: "Marcus T.",
                title: "VP of Operations, Mid-Market Manufacturing Co.",
                initials: "MT",
                stars: 5
              },
              {
                quote: "I've collaborated with Chris on multiple ERP rollouts. His ability to understand business operations at a deep level, while keeping people and culture at the center, is rare. He's the kind of buyer who actually cares about what happens after the deal closes.",
                name: "Sandra L.",
                title: "Senior ERP Consultant, Chicago",
                initials: "SL",
                stars: 5
              },
              {
                quote: "Chris has a unique combination of financial discipline and genuine empathy. He asks the right questions, listens carefully, and always follows through. If I were selling my business, he'd be my first call.",
                name: "David R.",
                title: "CEO, Technology Services Firm",
                initials: "DR",
                stars: 5
              },
              {
                quote: "Working alongside Chris for years, I've seen how he approaches complex problems — with calm, structure, and a long-term mindset. He doesn't chase quick wins. He builds lasting value.",
                name: "Jennifer M.",
                title: "Director of Finance, Regional Services Company",
                initials: "JM",
                stars: 5
              },
              {
                quote: "Chris brings a rare blend of technical expertise and business acumen. He understands systems, people, and cash flow — everything that matters when acquiring and growing a business.",
                name: "Robert K.",
                title: "IT Director, Healthcare Technology",
                initials: "RK",
                stars: 5
              },
              {
                quote: "I've referred several business owners to Chris for initial conversations. Every single one came back impressed by his professionalism, transparency, and genuine respect for what they'd built.",
                name: "Angela P.",
                title: "Business Broker & M&A Advisor",
                initials: "AP",
                stars: 5
              }
            ].map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-all duration-300 flex flex-col">
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.stars }).map((_, s) => (
                    <div key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-[#d4af37] text-base"></i>
                    </div>
                  ))}
                </div>
                <div className="w-8 h-8 flex items-center justify-center mb-4 flex-shrink-0">
                  <i className="ri-double-quotes-l text-[#d4af37] text-4xl opacity-60"></i>
                </div>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed italic flex-1 mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-4 mt-auto pt-5 border-t border-white/10">
                  <div className="w-11 h-11 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#d4af37] text-sm font-bold">{t.initials}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* LinkedIn CTA */}
          <div className="text-center">
            <a href="https://www.linkedin.com/in/profile1197" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-3 bg-white/10 border border-white/20 text-white px-7 py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-linkedin-fill text-[#d4af37] text-xl"></i>
              </div>
              View Full LinkedIn Profile & Recommendations
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="bg-white py-20 sm:py-28 lg:py-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 sm:mb-20">
            <span className="text-[#d4af37] text-xs font-bold tracking-[3px] uppercase">Common Questions</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2332] mt-4 mb-4">Frequently Asked Questions</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Selling a business is a big decision. Here are honest answers to the questions sellers ask most.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "How do you ensure confidentiality throughout the process?",
                a: "Confidentiality is my top priority. Before any meaningful information is exchanged, I sign a mutual Non-Disclosure Agreement (NDA). I never share your business details with third parties, and I conduct all conversations discreetly. Your employees, customers, and competitors will not know anything is happening until you decide to tell them."
              },
              {
                q: "How long does the acquisition process typically take?",
                a: "From initial conversation to closing, most deals take 60–90 days. The timeline depends on the complexity of the business and how quickly we can complete due diligence. I move decisively — there are no investment committees or approval chains slowing things down. Week 1: Initial call. Weeks 2–4: Due diligence. Week 5: Valuation. Weeks 6–8: Negotiation. Weeks 9–12: Closing & transition."
              },
              {
                q: "What deal structures are you open to?",
                a: "I'm very flexible. I'm open to all-cash deals, seller financing, earnouts tied to future performance, equity rollovers where you retain a stake, and hybrid structures. My goal is to find a structure that works for your financial situation, tax planning, and personal goals — not just mine."
              },
              {
                q: "Will you keep my employees after the acquisition?",
                a: "Absolutely. Retaining your team is one of my highest priorities. Your employees are a core part of what makes your business valuable. I have no interest in restructuring or laying people off. My model is to keep strong teams in place and support them — not replace them."
              },
              {
                q: "Do I need to stay involved after the sale?",
                a: "That's entirely up to you. Some sellers want a clean break; others prefer to stay on in an advisory or operational role for a transition period. I'm flexible and will work around your preferences. My goal is absentee or strategic ownership, so I'm not dependent on you staying — but I welcome it if you'd like to."
              },
              {
                q: "How do you value a business?",
                a: "I use a combination of EBITDA multiples, discounted cash flow analysis, and comparable market transactions. I'm transparent about my methodology and will walk you through every number. My goal is a fair valuation that reflects the true value of what you've built — not a lowball offer designed to exploit information asymmetry."
              },
              {
                q: "What industries are you most interested in?",
                a: "My background is in technology, so I have a natural affinity for IT services, MSPs, SaaS, and technology-enabled businesses. That said, I'm genuinely industry-agnostic. If your business has strong recurring revenue, solid cash flow, and a good management team, I want to hear about it — regardless of sector."
              },
              {
                q: "Is there any cost or obligation to having an initial conversation?",
                a: "None whatsoever. Our first conversation is completely free, confidential, and carries zero obligation. I'll listen to your situation, share my thoughts, and we'll both decide if it makes sense to move forward. There's no pressure and no sales pitch — just an honest conversation between two professionals."
              }
            ].map((item, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden hover:border-[#d4af37]/50 transition-colors">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5 sm:py-6 text-left cursor-pointer bg-white hover:bg-[#faf8f5] transition-colors"
                >
                  <span className="text-sm sm:text-base font-semibold text-[#1a2332] leading-snug">{item.q}</span>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#d4af37]/10 rounded-full">
                    <i className={`text-[#b8941f] text-lg transition-transform duration-300 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 sm:px-8 pb-6 sm:pb-7 bg-[#faf8f5] border-t border-gray-100">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed pt-4">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 sm:mt-16 bg-gradient-to-r from-[#1a2332] to-[#2d3748] rounded-2xl p-8 sm:p-10 text-center">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">Still Have Questions?</h3>
            <p className="text-gray-300 text-sm sm:text-base mb-6">I'm happy to answer anything on a no-obligation call. Let's talk.</p>
            <a href="#contact" className="inline-block bg-[#d4af37] text-[#1a2332] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#b8941f] hover:scale-105 transition-all whitespace-nowrap cursor-pointer">
              Schedule a Free Conversation
            </a>
            <p className="text-sm text-white/80 mt-5">Or email directly: <a href="mailto:chris@okotete-peterscapital.com" className="underline hover:text-[#d4af37] transition-colors">chris@okotete-peterscapital.com</a></p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-20 sm:py-32 lg:py-44 bg-gradient-to-br from-[#f5f3ef] to-[#faf8f5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="relative rounded-2xl sm:rounded-[32px] overflow-hidden shadow-2xl">
            <div className="absolute inset-0">
              <img src="https://public.readdy.ai/ai/img_res/a40e56ccad6055141487507df416b2fc.jpg" alt="Chicago Skyline" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            </div>
            <div className="relative z-10 px-6 sm:px-12 lg:px-16 py-16 sm:py-20 lg:py-24 text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white leading-tight mb-6 sm:mb-8">
                Ready to Discuss Your Business Transition? Let's Start a Conversation
              </h2>
              <p className="text-base sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
                Confidential, no-obligation discussion about your business and goals. Professional approach. Fair valuation. Respectful process.
              </p>
              <a href="#contact-form" className="inline-block bg-[#d4af37] text-[#1a2332] px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold shadow-xl hover:bg-[#b8941f] hover:scale-105 transition-all whitespace-nowrap cursor-pointer">
                Schedule Confidential Call
              </a>
            </div>
          </div>

          <div id="contact-form" className="mt-12 sm:mt-20 bg-white rounded-2xl shadow-xl p-6 sm:p-10 lg:p-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1a2332] mb-3 text-center">Submit Your Opportunity</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-8 sm:mb-10 text-center">Fill out the form below and I'll get back to you within 24 hours</p>
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-5 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm" placeholder="John Smith" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm" placeholder="john@company.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm" placeholder="Your Company LLC" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm" placeholder="(312) 555-0123" />
                </div>
              </div>
              <div>
                <label htmlFor="ebitda" className="block text-sm font-semibold text-gray-700 mb-2">Estimated EBITDA Range *</label>
                <select id="ebitda" name="ebitda" value={formData.ebitda} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm cursor-pointer">
                  <option value="">Select EBITDA Range</option>
                  <option value="600k-1m">$600K – $1M</option>
                  <option value="1m-2m">$1M – $2M</option>
                  <option value="2m-3m">$2M – $3M</option>
                  <option value="3m+">$3M+</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Tell Me About Your Business *</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required maxLength={500} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all resize-none text-sm" placeholder="Brief description of your business, industry, years in operation, and why you're considering selling..."></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>
              <button type="submit" disabled={formStatus === 'submitting'} className="w-full bg-[#1a2332] text-white py-4 rounded-lg text-base font-bold hover:bg-[#2d3748] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                {formStatus === 'submitting' ? 'Submitting...' : 'Submit Opportunity'}
              </button>
              {formStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-5 py-4 rounded-lg text-sm">
                  <strong>Thank you!</strong> Your submission has been received. I'll review your information and get back to you within 24 hours.
                </div>
              )}
              {formStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-lg text-sm">
                  <strong>Error:</strong> There was a problem submitting your form. Please try again or email me directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0f1a] text-white py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 sm:gap-12 mb-12 sm:mb-16">
            <div className="sm:col-span-2 lg:col-span-5">
              <h3 className="text-3xl sm:text-4xl font-light mb-2">Let's Build Something</h3>
              <h3 className="text-3xl sm:text-4xl font-light mb-6 sm:mb-8">Together</h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-6 sm:mb-8">
                Professional business acquisition focused on preserving legacy and driving strategic growth.
              </p>
              <div className="flex items-center gap-3">
                <input type="email" placeholder="Your email for updates" className="flex-1 bg-transparent border-b border-white/30 py-3 px-2 text-white placeholder-gray-500 focus:border-[#d4af37] outline-none transition-colors text-sm min-w-0" />
                <button className="bg-white text-[#1a2332] px-4 sm:px-6 py-3 rounded-full font-semibold hover:bg-[#d4af37] transition-all flex items-center gap-2 whitespace-nowrap text-sm cursor-pointer flex-shrink-0">
                  Subscribe
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
            <div className="lg:col-span-3">
              <h4 className="text-xs font-bold text-[#d4af37] uppercase tracking-wider mb-5">Quick Links</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li><a href="#criteria" className="text-sm sm:text-base text-white hover:text-[#d4af37] transition-colors underline">Acquisition Criteria</a></li>
                <li><a href="#why-me" className="text-sm sm:text-base text-white hover:text-[#d4af37] transition-colors underline">Why Sell to Chris</a></li>
                <li><a href="#testimonials" className="text-sm sm:text-base text-white hover:text-[#d4af37] transition-colors underline">Testimonials</a></li>
                <li><a href="#faq" className="text-sm sm:text-base text-white hover:text-[#d4af37] transition-colors underline">FAQ</a></li>
                <li><a href="#contact" className="text-sm sm:text-base text-white hover:text-[#d4af37] transition-colors underline">Submit Opportunity</a></li>
              </ul>
            </div>
            <div className="lg:col-span-4">
              <h4 className="text-xs font-bold text-[#d4af37] uppercase tracking-wider mb-3">Location</h4>
              <p className="text-sm sm:text-base text-gray-400">Chicago, Illinois</p>
              <p className="text-sm sm:text-base text-gray-400">United States</p>
            </div>
          </div>
          <div className="border-t border-[#d4af37]/30 pt-7 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-6 text-center sm:text-left">
              <a href="https://www.linkedin.com/in/profile1197" target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#d4af37] transition-colors underline">
                <i className="ri-linkedin-fill text-lg"></i>
                Connect on LinkedIn
              </a>
              <p className="text-sm text-gray-400">© 2025 Chris Okotete-Peters. All rights reserved.</p>
              <div className="flex gap-5 sm:gap-6">
                <a href="#" className="text-sm text-gray-400 hover:text-[#d4af37] transition-colors underline">Privacy Policy</a>
                <a href="#" className="text-sm text-gray-400 hover:text-[#d4af37] transition-colors underline">Terms</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
